import React, { useEffect, useState, useRef } from 'react';
import { Plus, MapPin, ChevronRight, Edit2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile'; 
import { CitySearchCombobox } from '../ui/combobox/CitySearchCombobox';
import { normalizeCity } from '@/lib/city/normalizer';

const QuickPlayWelcome: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { profile, fetchProfile, updateProfile } = useProfile();
  const [isEditingCity, setIsEditingCity] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [isSavingCity, setIsSavingCity] = useState(false);
  const [showCityOptions, setShowCityOptions] = useState(false);
  const cityOptionsRef = useRef<HTMLDivElement>(null);
  const cityButtonRef = useRef<HTMLDivElement>(null);

const greetings = [
  "Hi",        // English
  "Ciao",      // Italian
  "Hola",      // Spanish
  "Salut",     // French
  "Hallo",     // German
  "Olá",       // Portuguese
  "Hej",       // Swedish/Danish
  "שלום",      // Hebrew
  "مرحبا",     // Arabic
  "こんにちは", // Japanese
  "नमस्ते",     // Hindi
  "안녕하세요", // Korean
  "Привет",    // Russian
  "你好",       // Chinese (Mandarin)
  "Selam",     // Turkish
  "Szia",      // Hungarian
  "Sawubona"   // Zulu
];
  const [greetingIndex, setGreetingIndex] = useState(0);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    const interval = setInterval(() => {
      setGreetingIndex((prev) => (prev + 1) % greetings.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  // Handle click outside to close city options dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCityOptions && 
        cityOptionsRef.current && 
        cityButtonRef.current && 
        !cityOptionsRef.current.contains(event.target as Node) &&
        !cityButtonRef.current.contains(event.target as Node)
      ) {
        setShowCityOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCityOptions]);

  const firstName = profile?.full_name?.split(' ')[0] || 'there';
  const currentCity = profile?.city || 'your city';

  const handleNavigateToCityPage = () => {
    const normalizedCity = normalizeCity(currentCity);
    navigate('/dashboard/facilities', {
      state: { selectedCity: normalizedCity }
    });
  };

  const handleCityChange = async (city: string) => {
    try {
      setIsSavingCity(true);
      setCityError(null);
      
      await updateProfile({ city });
      
      // Refresh profile data
      await fetchProfile();
      
      // Trigger dashboard components refresh
      window.dispatchEvent(new CustomEvent('city-changed', { detail: { city } }));
      
      setIsEditingCity(false);
    } catch (err) {
      console.error('Failed to update city:', err);
      setCityError('Failed to update city. Please try again.');
    } finally {
      setIsSavingCity(false);
    }
  };

  const handleStartPlaying = () => {
    localStorage.setItem('quickplay_welcomed', 'true');
    navigate('/dashboard/quick-play-transition', {
      replace: true,
      state: { fromNav: true, searchKey: Date.now() },
    });
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-dark-200 rounded-xl p-4 border border-dark-300 hover:border-gold-400/30 transition-all duration-300 mb-4"
    >
      <div className="flex items-center">
        <button
          onClick={handleStartPlaying}
          className="w-10 h-10 bg-gold-400 hover:bg-gold-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-transform hover:scale-105 active:scale-95 shadow-md"
        >
          <Plus className="h-5 w-5 text-dark-50" />
        </button>

        <div className="flex-1">
          <div className="flex items-center">
            <AnimatePresence mode="wait">
              <motion.span
                key={greetings[greetingIndex]}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
                className="inline-block mr-1 text-white font-bold text-xl"
              >
                {greetings[greetingIndex]}
              </motion.span>
            </AnimatePresence>
            <span className="text-white font-bold text-xl">{firstName}!</span>
          </div>
          <div className="flex items-center">
            <span className="text-white">Welcome to </span>
            {isEditingCity ? (
              <div className="inline-block relative z-50 ml-1">
                <CitySearchCombobox
                  value={currentCity}
                  onChange={handleCityChange}
                  placeholder="Enter city..."
                  className="w-40 inline-block"
                  error={cityError}
                />
                {isSavingCity && (
                  <div className="absolute inset-0 flex items-center justify-center bg-dark-300/50 backdrop-blur-sm rounded-lg">
                    <div className="w-4 h-4 border-2 border-t-transparent border-gold-400 rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative ml-1">
                <div 
                  className="flex items-center cursor-pointer group"
                  ref={cityButtonRef}
                  onClick={() => setShowCityOptions(!showCityOptions)}
                >
                  <MapPin className="h-4 w-4 text-gold-400 mr-1 group-hover:animate-pulse" />
                  <span className="text-gold-400 font-medium text-lg group-hover:text-gold-300 transition-colors">
                    {currentCity}
                  </span>
                  <ChevronRight className={cn(
                    "h-4 w-4 text-gold-400 ml-1 transition-transform duration-200",
                    showCityOptions ? "rotate-90" : ""
                  )} />
                </div>
                
                {/* City Options Dropdown */}
                {showCityOptions && (
                  <div 
                    ref={cityOptionsRef}
                    className={cn(
                    "absolute left-0 mt-1 w-[220px] sm:w-auto sm:min-w-[200px] z-50 rounded-lg shadow-lg overflow-hidden",
                    "border border-dark-400 bg-dark-300 animate-in fade-in-50 slide-in-from-top-5"
                  )}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigateToCityPage();
                        setShowCityOptions(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-dark-400 text-white flex items-center whitespace-nowrap"
                    >
                      <MapPin className="h-4 w-4 mr-2 text-gold-400" />
                      <span>View {currentCity} page</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingCity(true);
                        setShowCityOptions(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-dark-400 text-white flex items-center whitespace-nowrap"
                    >
                      <Edit2 className="h-4 w-4 mr-2 text-gold-400" />
                      <span>Change city</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-dark-800 text-sm">
            Meet club members in town for sports or wellness. It's that simple! 🏃⚽ 🎾🧘‍♀️
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default QuickPlayWelcome;